<template>
  <div class="device-edit-container">
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-edit"></i>
          <span>{{ $t('device.deviceEdit') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button @click="goBack" size="small">
            <i class="el-icon-back"></i> {{ $t('common.back') }}
          </el-button>
        </div>
      </div>
    </div>

    <el-card class="device-edit-card" shadow="hover">
      <el-form ref="deviceForm" :model="deviceForm" :rules="rules" label-position="top" size="large">
        <div class="form-module">
           <div class="module-title">{{ $t('device.basicInfo') }} <el-tag size="small" type="warning" style="margin-left: 10px">智能设备</el-tag></div>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('device.deviceCode')" prop="deviceCode">
                <el-input v-model="deviceForm.deviceCode" :placeholder="$t('device.deviceCode')" disabled />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="设备种类" prop="categoryLabel">
                <el-input v-model="deviceCategoryLabel" disabled />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('device.deviceName')" prop="deviceName">
                <el-input v-model="deviceForm.deviceName" :placeholder="$t('device.deviceName')" disabled />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" v-if="deviceForm.productType === 'DEVICE'">
              <el-form-item :label="$t('device.macAddress')" prop="macAddress">
                <el-input v-model="deviceForm.macAddress" :placeholder="$t('device.macAddress')" disabled />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" v-if="deviceForm.productType === 'DEVICE'">
              <el-form-item :label="$t('device.deviceModel')" prop="board">
                <el-select v-model="deviceForm.board" :placeholder="$t('device.deviceModel')" clearable filterable style="width: 100%">
                  <el-option
                    v-for="item in boards"
                    :key="item.key"
                    :label="item.name"
                    :value="item.key"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" v-if="deviceForm.productType === 'DEVICE'">
              <el-form-item :label="$t('device.firmwareVersion')" prop="appVersion">
                <el-input v-model="deviceForm.appVersion" :placeholder="$t('device.firmwareVersion')" clearable />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <div class="form-module">
          <div class="module-title">{{ $t('device.statusInfo') }}</div>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="设备音量" prop="volume">
                <div class="volume-control">
                  <el-button
                    class="volume-mute-btn"
                    :icon="deviceForm.volume === 0 ? 'el-icon-turn-off-microphone' : 'el-icon-microphone'"
                    circle
                    size="mini"
                    :type="deviceForm.volume === 0 ? 'danger' : 'default'"
                    @click="toggleMute"
                    :title="deviceForm.volume === 0 ? '取消静音' : '静音'"
                  />
                  <el-slider
                    v-model="deviceForm.volume"
                    :min="0"
                    :max="100"
                    :format-tooltip="formatVolumeTooltip"
                    :class="['volume-slider', { 'is-muted': deviceForm.volume === 0 }]"
                    @change="onVolumeChange"
                  />
                  <span class="volume-value">
                    <i v-if="volumeSending" class="el-icon-loading volume-loading-icon"></i>
                    {{ deviceForm.volume }}%
                  </span>
                </div>
                <div v-if="volumeError" class="volume-error-tip">
                  <i class="el-icon-warning"></i> {{ volumeError }}
                </div>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="WIFI名称" prop="wifiSsid">
                <el-input v-model="deviceForm.wifiSsid" placeholder="请输入WIFI名称" clearable />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" v-if="deviceForm.productType === 'DEVICE'">
              <el-form-item label="WIFI密码" prop="wifiPassword">
                <el-input
                  v-model="deviceForm.wifiPassword"
                  :type="wifiPasswordVisible ? 'text' : 'password'"
                  placeholder="请输入WIFI密码"
                  clearable
                >
                  <i
                    slot="suffix"
                    :class="wifiPasswordVisible ? 'el-icon-view' : 'el-icon-hide'"
                    style="cursor: pointer; line-height: 32px;"
                    @click="wifiPasswordVisible = !wifiPasswordVisible"
                  ></i>
                </el-input>
                <div class="wifi-password-tip">
                  <i class="el-icon-info"></i>
                  <span>密码长度至少8位，保存后将同步到设备</span>
                </div>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item :label="$t('device.autoUpdate')" prop="autoUpdate">
                <el-switch
                  v-model="deviceForm.autoUpdate"
                  :active-value="1"
                  :inactive-value="0"
                  active-color="#13ce66"
                  inactive-color="#ff4949"
                />
              </el-form-item>
            </el-col>
          </el-row>
        </div>

        <div class="form-module">
          <div class="module-title">智能体配置</div>
          <el-row :gutter="20">
            <el-col :xs="24" :sm="12" :md="8">
              <el-form-item label="关联智能体" prop="agentId">
                <el-select
                  v-model="deviceForm.agentId"
                  placeholder="请选择智能体"
                  clearable
                  filterable
                  style="width: 100%"
                  :loading="loadingAgents"
                  no-match-text="无匹配智能体"
                  no-data-text="该用户暂无智能体"
                >
                  <el-option
                    v-for="agent in agentList"
                    :key="agent.id"
                    :label="agent.agentName"
                    :value="agent.id"
                  >
                    <span>{{ agent.agentName }}</span>
                    <span style="float: right; color: #8492a6; font-size: 12px;">{{ agent.agentCode || '' }}</span>
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <div v-if="!deviceForm.userId" class="agent-tip">
            <i class="el-icon-info"></i>
            <span>设备未绑定用户，无法选择智能体。请先绑定用户后再配置智能体。</span>
          </div>
        </div>

        <div class="form-module">
          <div class="module-title">{{ $t('device.otherInfo') }}</div>
          <el-form-item :label="$t('device.deviceRemark')" prop="deviceRemark">
            <el-input
              type="textarea"
              v-model="deviceForm.deviceRemark"
              :rows="4"
              :placeholder="$t('device.deviceRemark')"
              clearable
            />
          </el-form-item>
        </div>
      </el-form>

      <div class="form-actions">
        <el-button @click="goBack">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" @click="handleSave" :loading="loading">{{ $t('common.save') }}</el-button>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getDeviceDetail, updateDevice, controlDeviceVolume, controlDeviceWifi } from '@/apis/device/device'
import { getSkuDetail } from '@/apis/product/sku'
import { getDictDataByType } from '@/apis/system/dict'
import request from '@/utils/request'

export default {
  name: 'DeviceEdit',
  data() {
    return {
      loading: false,
      categoryLoading: false,
      categoryOptions: [],
      deviceCategoryLabel: '',
      deviceForm: {
        id: '',
        productType: 'DEVICE',
        category: '',
        deviceName: '',
        deviceCode: '',
        macAddress: '',
        board: '',
        appVersion: '',
        autoUpdate: 0,
        volume: 50,
        wifiSsid: '',
        wifiPassword: '',
        deviceRemark: '',
        agentId: '',
        userId: null
      },
      volumeBeforeMute: 50,
      volumeSending: false,
      volumeError: '',
      volumeDebounceTimer: null,
      wifiPasswordVisible: false,
      wifiSending: false,
      originalWifiSsid: '',
      originalWifiPassword: '',
      boards: [],
      peripheralTypes: [],
      agentList: [],
      loadingAgents: false,
      rules: {
        board: [
          { 
            required: true, 
            validator: (rule, value, callback) => {
              if (this.deviceForm.productType === 'DEVICE' && !value) {
                callback(new Error(this.$t('device.deviceModelRequired')))
              } else {
                callback()
              }
            }, 
            trigger: 'change' 
          }
        ]
      }
    }
  },
  created() {
    this.fetchDeviceDetail()
    this.fetchBoards()
    this.fetchPeripheralTypes()
  },
  beforeDestroy() {
    if (this.volumeDebounceTimer) {
      clearTimeout(this.volumeDebounceTimer)
    }
  },
  methods: {
    // 切换静音
    toggleMute() {
      if (this.deviceForm.volume > 0) {
        this.volumeBeforeMute = this.deviceForm.volume
        this.deviceForm.volume = 0
      } else {
        this.deviceForm.volume = this.volumeBeforeMute || 50
      }
      this.onVolumeChange()
    },

    // 格式化音量提示
    formatVolumeTooltip(val) {
      if (val === 0) return '静音'
      if (val <= 30) return `${val}% (低)`
      if (val <= 70) return `${val}% (中)`
      return `${val}% (高)`
    },

    // 音量滑块变化事件（防抖处理）
    onVolumeChange() {
      this.volumeError = ''
      // 防抖：用户停止拖动500ms后发送指令
      if (this.volumeDebounceTimer) {
        clearTimeout(this.volumeDebounceTimer)
      }
      this.volumeDebounceTimer = setTimeout(() => {
        this.sendVolumeCommand(this.deviceForm.volume)
      }, 500)
    },

    // 发送音量控制指令到设备
    async sendVolumeCommand(volume) {
      if (!this.deviceForm.id) return

      this.volumeSending = true
      this.volumeError = ''

      try {
        await controlDeviceVolume(this.deviceForm.id, volume)
        this.$message({
          message: `音量已调整为 ${volume}%`,
          type: 'success',
          duration: 1500
        })
      } catch (error) {
        // 提取错误信息
        let errorMsg = '音量调整失败'
        if (error && error.message) {
          errorMsg = error.message
        } else if (error && error.msg) {
          errorMsg = error.msg
        }

        // 根据错误类型提供更友好的提示
        if (errorMsg.includes('不在线') || errorMsg.includes('offline')) {
          errorMsg = '设备不在线，无法实时调整音量。保存设置后，设备下次连接时将自动应用。'
        } else if (errorMsg.includes('MCP') || errorMsg.includes('就绪')) {
          errorMsg = '设备MCP服务未就绪，请稍后重试'
        } else if (errorMsg.includes('连接') || errorMsg.includes('connect')) {
          errorMsg = '无法连接到设备服务，请检查服务状态'
        }

        this.volumeError = errorMsg
        this.$message({
          message: errorMsg,
          type: 'warning',
          duration: 3000
        })
      } finally {
        this.volumeSending = false
      }
    },

    async fetchCategoryOptions() {
      if (!this.deviceForm.productType) return
      this.categoryLoading = true
      try {
        const dictType = this.deviceForm.productType === 'DEVICE' ? 'DEVICE_TYPE' : 'PERIPHERAL_TYPE'
        const res = await getDictDataByType(dictType)
        this.categoryOptions = res.data || res || []
      } catch (e) {
        console.error('Failed to fetch category options', e)
        this.categoryOptions = []
      } finally {
        this.categoryLoading = false
      }
    },
    async fetchPeripheralTypes() {
      try {
        const res = await getDictDataByType('PERIPHERAL_TYPE')
        const data = res.data || res
        if (data) {
          this.peripheralTypes = data
        }
      } catch (error) {
        console.error('获取周边类型失败:', error)
      }
    },
    // 返回上一页
    goBack() {
      this.$router.back()
    },

    // 获取设备型号字典
    async fetchBoards() {
      try {
        const data = await getDictDataByType('FIRMWARE_TYPE')
        this.boards = data || []
      } catch (error) {
        console.error('获取设备型号字典失败:', error)
      }
    },

    // 获取设备详情
    async fetchDeviceDetail() {
      this.loading = true
      try {
        const deviceId = this.$route.params.id
        const data = await getDeviceDetail(deviceId)
        if (data) {
          this.deviceForm = {
            id: data.id,
            productType: data.productType || 'DEVICE',
            category: data.category ? data.category.toString() : '',
            macAddress: data.macAddress,
            deviceName: data.deviceName,
            deviceCode: data.deviceCode,
            board: data.board,
            appVersion: data.appVersion,
            autoUpdate: data.autoUpdate || 0,
            volume: data.volume !== null && data.volume !== undefined ? data.volume : 50,
            wifiSsid: data.wifiSsid || '',
            wifiPassword: '',
            deviceRemark: data.deviceRemark,
            agentId: data.agentId || '',
            userId: data.userId || null
          }
          
          // 保存原始WiFi信息用于判断是否需要同步到设备
          this.originalWifiSsid = data.wifiSsid || ''
          this.originalWifiPassword = ''
          
          // 设置设备种类标签
          await this.fetchCategoryOptions()
          if (this.categoryOptions.length > 0) {
            const matchedCategory = this.categoryOptions.find(item => item.key === this.deviceForm.category)
            this.deviceCategoryLabel = matchedCategory ? matchedCategory.name : '未知类型'
          }

          // 加载设备绑定用户的智能体列表
          await this.fetchAgents()
        }
      } catch (error) {
        console.error('获取设备详情失败:', error)
        this.$message.error(this.$t('device.getDeviceDetailFailed'))
      } finally {
        this.loading = false
      }
    },

    // 获取设备绑定用户的智能体列表
    async fetchAgents() {
      if (!this.deviceForm.userId) {
        this.agentList = []
        return
      }
      this.loadingAgents = true
      try {
        const res = await request({
          url: '/agent/all',
          method: 'get',
          params: {
            userId: this.deviceForm.userId,
            limit: 1000
          }
        })
        this.agentList = res.list || []
      } catch (error) {
        console.error('获取智能体列表失败:', error)
        try {
          const res = await request({
            url: '/agent/list',
            method: 'get',
            params: { userId: this.deviceForm.userId }
          })
          this.agentList = Array.isArray(res) ? res : (res.list || [])
        } catch (err) {
          console.error('获取智能体列表失败(降级):', err)
          this.agentList = []
        }
      } finally {
        this.loadingAgents = false
      }
    },

    // 保存设备信息
    async handleSave() {
      if (this.deviceForm.category === 2) {
        this.$refs.deviceForm.clearValidate(['board']);
      }

      this.$refs.deviceForm.validate(async (valid) => {
        if (valid) {
          this.$confirm(this.$t('device.saveConfirm'), this.$t('common.tips'), {
            confirmButtonText: this.$t('common.confirm'),
            cancelButtonText: this.$t('common.cancel'),
            type: 'warning'
          }).then(async () => {
            this.loading = true
            try {
              // 提交数据时只包含有效字段
              const submitData = {
                id: this.deviceForm.id,
                productType: this.deviceForm.productType,
                category: this.deviceForm.category,
                deviceCode: this.deviceForm.deviceCode,
                board: this.deviceForm.productType === 'DEVICE' ? this.deviceForm.board : null,
                appVersion: this.deviceForm.appVersion,
                autoUpdate: this.deviceForm.autoUpdate,
                volume: this.deviceForm.volume,
                wifiSsid: this.deviceForm.wifiSsid,
                wifiPassword: this.deviceForm.wifiPassword || null,
                deviceRemark: this.deviceForm.deviceRemark,
                agentId: this.deviceForm.agentId || null
              }
              await updateDevice(this.deviceForm.id, submitData)

              // 如果WiFi信息有变化，同步到设备
              await this.syncWifiToDevice()

              this.$message.success(this.$t('device.saveSuccess'))
              this.$router.push('/device')
            } catch (error) {
              console.error('保存设备信息失败:', error)
              this.$message.error(this.$t('device.saveFailed'))
            } finally {
              this.loading = false
            }
          }).catch(() => {})
        } else {
          this.$message.error(this.$t('common.formValidateFailed'))
          return false
        }
      })
    },

    // 同步WiFi信息到设备
    async syncWifiToDevice() {
      const ssid = this.deviceForm.wifiSsid?.trim()
      const password = this.deviceForm.wifiPassword

      // 没有填写SSID，不同步
      if (!ssid) {
        return
      }

      // 判断WiFi信息变化情况
      const originalSsid = this.originalWifiSsid
      const isNewWifi = !originalSsid
      const isSsidChanged = originalSsid && originalSsid !== ssid
      const hasPassword = password && password.length > 0

      // 没有变化则不同步（SSID未变且无新密码输入）
      if (!isNewWifi && !isSsidChanged && !hasPassword) {
        return
      }

      this.wifiSending = true
      try {
        // 场景1: 新增WiFi（原无WiFi配置）→ 使用 add_and_switch 组合动作
        if (isNewWifi) {
          if (!hasPassword) {
            return
          }
          console.log(`[WiFi同步] 新增WiFi并切换 - ssid=${ssid}`)
          await controlDeviceWifi(this.deviceForm.id, 'add_and_switch', {
            ssid: ssid,
            password: password
          })
          this.$message({
            message: `WiFi [${ssid}] 已添加并切换`,
            type: 'success',
            duration: 2000
          })
        }
        // 场景2: SSID变更（添加新WiFi+切换 + 移除旧WiFi）
        else if (isSsidChanged) {
          if (!hasPassword) {
            return
          }
          console.log(`[WiFi同步] SSID变更并切换 - 旧=${originalSsid} 新=${ssid}`)
          // 先添加新WiFi并切换（原子操作，添加成功才会切换）
          await controlDeviceWifi(this.deviceForm.id, 'add_and_switch', {
            ssid: ssid,
            password: password
          })
          // 再移除旧WiFi（失败不阻断，仅警告）
          try {
            await controlDeviceWifi(this.deviceForm.id, 'remove', {
              ssid: originalSsid
            })
            console.log(`[WiFi同步] 旧WiFi [${originalSsid}] 已移除`)
          } catch (removeErr) {
            console.warn(`[WiFi同步] 移除旧WiFi [${originalSsid}] 失败:`, removeErr)
          }
          this.$message({
            message: `WiFi已切换: ${originalSsid} → ${ssid}`,
            type: 'success',
            duration: 2000
          })
        }
        // 场景3: SSID未变，仅更新密码 → 使用 update_and_switch 组合动作
        else {
          if (!hasPassword) {
            return
          }
          console.log(`[WiFi同步] 更新密码并切换 - ssid=${ssid}`)
          await controlDeviceWifi(this.deviceForm.id, 'update_and_switch', {
            ssid: ssid,
            password: password
          })
          this.$message({
            message: `WiFi [${ssid}] 密码已更新并切换`,
            type: 'success',
            duration: 2000
          })
        }

        // 更新原始SSID记录
        this.originalWifiSsid = ssid
      } catch (error) {
        let errorMsg = 'WiFi同步到设备失败'
        if (error && error.message) {
          errorMsg = error.message
        }

        // 设备离线时的友好提示
        if (errorMsg.includes('不在线') || errorMsg.includes('offline')) {
          errorMsg = '设备不在线，WiFi配置将在设备下次连接时同步'
          this.$message({
            message: errorMsg,
            type: 'warning',
            duration: 3000
          })
        } else {
          this.$message({
            message: errorMsg,
            type: 'error',
            duration: 3000
          })
        }
      } finally {
        this.wifiSending = false
      }
    }
  }
}
</script>

<style scoped>
.device-edit-container {
  padding: 0;
  flex-shrink: 0;
}

.control-panel {
  margin-bottom: 24px;
}

.control-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #303133;
  font-size: 16px;
  font-weight: 500;
  
  i {
    font-size: 20px;
    color: #409eff;
  }
}

.action-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.device-edit-card {
  margin-bottom: 20px;
  border-radius: 8px;
}

.form-module {
  margin-bottom: 30px;
}

.module-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 20px;
  padding-left: 10px;
  border-left: 4px solid #409eff;
  line-height: 1;
}

.form-actions {
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
  text-align: right;
}

.form-actions button {
  min-width: 100px;
  margin-left: 15px;
}

.agent-tip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #fdf6ec;
  border-radius: 4px;
  color: #e6a23c;
  font-size: 13px;
  margin-top: 4px;

  i {
    font-size: 16px;
  }
}

.volume-control {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.volume-mute-btn {
  flex-shrink: 0;
}

.volume-slider {
  flex: 1;
}

.volume-slider.is-muted {
  :deep(.el-slider__runway) {
    background-color: #f5f5f5;
  }
  :deep(.el-slider__bar) {
    background-color: #c0c4cc;
  }
}

.volume-value {
  flex-shrink: 0;
  min-width: 40px;
  text-align: right;
  font-size: 13px;
  color: #606266;
  font-weight: 500;
}

.volume-loading-icon {
  color: #409eff;
  animation: rotating 1.5s linear infinite;
}

@keyframes rotating {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.volume-error-tip {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 12px;
  color: #e6a23c;
  line-height: 1.4;

  i {
    font-size: 14px;
    flex-shrink: 0;
  }
}

.wifi-password-tip {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;

  i {
    font-size: 14px;
    flex-shrink: 0;
  }
}

:deep(.el-form-item__label) {
  font-weight: 500;
  color: #606266;
  padding-bottom: 8px;
}

:deep(.el-input__inner), :deep(.el-textarea__inner) {
  border-radius: 4px;
}

@media screen and (max-width: 768px) {
  .control-container {
    flex-direction: row;
    align-items: center;
  }
  
  .header-info span {
    display: none;
  }
}
</style>